import { zodResolver } from "@hookform/resolvers/zod";
import { startCase } from "lodash";
import moment from "moment";
import {
  type SubmitErrorHandler,
  type SubmitHandler,
  useForm,
} from "react-hook-form";
import { z } from "zod";

import {
  Button,
  DatePicker,
  Input,
  Modal,
  Select,
  toastError,
} from "@alpacahq/ui/old";

import {
  BENEFICIARY_RELATIONSHIPS,
  type Beneficiary,
} from "~/v2/api/rest/account";
import {
  buildTaxIdSchema,
  onTaxIdKeyDown,
  refineTaxId,
  TAX_ID_TYPE_LABELS,
  type TaxIdType,
} from "../taxIdValidation";

const REQUIRED_ERROR_MSG = "Complete required field.";

const BENEFICIARY_TAX_ID_TYPES: readonly [TaxIdType, ...TaxIdType[]] = [
  "USA_SSN",
];

const schema = z
  .object({
    given_name: z.string().min(1, REQUIRED_ERROR_MSG),
    family_name: z.string().min(1, REQUIRED_ERROR_MSG),
    relationship: z.enum(BENEFICIARY_RELATIONSHIPS, {
      error: REQUIRED_ERROR_MSG,
    }),
    date_of_birth: z.string({ error: REQUIRED_ERROR_MSG }).date(),
  })
  .merge(buildTaxIdSchema(BENEFICIARY_TAX_ID_TYPES))
  .superRefine(refineTaxId);

export type FormBeneficiary = z.infer<typeof schema>;

interface AddBeneficiaryModalProps {
  onClose: () => void;
  onSave: (beneficiary: FormBeneficiary) => void;
  editBeneficiary?: Beneficiary | null;
}

export const BeneficiaryModal = ({
  onClose,
  onSave,
  editBeneficiary,
}: AddBeneficiaryModalProps) => {
  const {
    watch,
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormBeneficiary>({
    resolver: zodResolver(
      schema.superRefine((data, ctx) => {
        const dateOfBirth = moment(data.date_of_birth, "yyyy-MM-DD");
        if (!dateOfBirth.isValid()) {
          ctx.addIssue({
            path: ["date_of_birth"],
            code: z.ZodIssueCode.custom,
            message: "Invalid date of birth",
          });
        }
        if (dateOfBirth.isAfter(moment())) {
          ctx.addIssue({
            path: ["date_of_birth"],
            code: z.ZodIssueCode.custom,
            message: "Date of birth must be before today",
          });
        }
        if (dateOfBirth.isBefore("1900-01-01")) {
          ctx.addIssue({
            path: ["date_of_birth"],
            code: z.ZodIssueCode.custom,
            message: "Date of birth must be after 1900-01-01",
          });
        }
      }),
    ),
    defaultValues: {
      relationship: editBeneficiary?.relationship ?? undefined,
      date_of_birth: editBeneficiary?.date_of_birth ?? "",
      tax_id_type:
        (editBeneficiary?.tax_id_type as TaxIdType | undefined) ??
        (BENEFICIARY_TAX_ID_TYPES.length === 1
          ? BENEFICIARY_TAX_ID_TYPES[0]
          : undefined),
      tax_id: editBeneficiary?.tax_id ?? "",
      given_name: editBeneficiary?.given_name ?? "",
      family_name: editBeneficiary?.family_name ?? "",
    },
  });

  const relationship = watch("relationship");
  const dateOfBirth = watch("date_of_birth");
  const taxIdType = watch("tax_id_type");

  const onSubmitValid: SubmitHandler<FormBeneficiary> = (data) => {
    onSave(data);
  };

  const onSubmitInvalid: SubmitErrorHandler<FormBeneficiary> = () => {
    toastError("Please fix the errors in the form.");
  };

  return (
    <Modal
      title={editBeneficiary ? "Edit Beneficiary" : "Add Beneficiary"}
      isOpen={true}
      onClose={onClose}
    >
      <form onSubmit={handleSubmit(onSubmitValid, onSubmitInvalid)}>
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2">
            <Input
              label="First Name"
              {...register("given_name")}
              errorMsg={errors.given_name?.message}
              status={errors.given_name?.message ? "error" : "neutral"}
            />
            <Input
              label="Last Name"
              {...register("family_name")}
              errorMsg={errors.family_name?.message}
              status={errors.family_name?.message ? "error" : "neutral"}
            />
          </div>
          <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2">
            <DatePicker
              label="Date of Birth"
              value={dateOfBirth}
              onChange={(date) => date && setValue("date_of_birth", date)}
              errorMsg={errors.date_of_birth?.message}
              status={errors.date_of_birth?.message ? "error" : "neutral"}
            />
            {/** Only show Tax ID Type dropdown when we support more than 1 tax id type  */}
            {BENEFICIARY_TAX_ID_TYPES.length > 1 && (
              <Select
                multiple={false}
                label="Tax ID Type"
                options={BENEFICIARY_TAX_ID_TYPES.map((type) => ({
                  label: TAX_ID_TYPE_LABELS[type],
                  value: type,
                }))}
                value={taxIdType}
                onChange={(value) => setValue("tax_id_type", value)}
                errorMsg={errors.tax_id_type?.message}
                status={errors.tax_id_type?.message ? "error" : "neutral"}
              />
            )}
          </div>
          <div className="flex flex-col gap-4 sm:grid sm:grid-cols-2">
            <Input
              label={
                taxIdType ? TAX_ID_TYPE_LABELS[taxIdType] : "Tax ID Number"
              }
              {...register("tax_id")}
              onKeyDown={onTaxIdKeyDown}
              errorMsg={errors.tax_id?.message}
              status={errors.tax_id?.message ? "error" : "neutral"}
            />
            <Select
              multiple={false}
              label="Relationship to you"
              options={BENEFICIARY_RELATIONSHIPS.map((relationship) => ({
                label: startCase(relationship),
                value: relationship,
              }))}
              value={relationship}
              onChange={(value) => setValue("relationship", value)}
              errorMsg={errors.relationship?.message}
              status={errors.relationship?.message ? "error" : "neutral"}
            />
          </div>
          <div className="flex items-center justify-end gap-4">
            <Button variant="v2/stroke" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button
              variant="v2/filled"
              onClick={handleSubmit(onSubmitValid, onSubmitInvalid)}
              type="submit"
            >
              {editBeneficiary ? "Save" : "Add Beneficiary"}
            </Button>
          </div>
        </div>
      </form>
    </Modal>
  );
};
